import { action } from 'mobx';
import { SessionStore } from './session.store';
import { AggregateFunction, AgtAnalyticsStore, SelectColumn } from './agt.analytics.store';

export interface IAgtLeadNowConvModel {
	vtnrId: number;
	kw: number;
	brutto: number;
	netto: number;
	converted: number;
	neuStck: number;
	neuBew: number;
}

/** Aggregate funnel totals (one benchmark population over a kw range). */
export interface ILeadNowConvTotals {
	brutto: number;
	netto: number;
	converted: number;
	neuStck: number;
}

/** Response of the lead-conversion benchmark endpoint. */
export interface ILeadNowConvBenchmark {
	vd?: ILeadNowConvTotals;
	abv?: ILeadNowConvTotals;
}

export class AgtLeadNowConvModel implements IAgtLeadNowConvModel {
	vtnrId: number;
	kw: number;
	brutto: number;
	netto: number;
	converted: number;
	neuStck: number;
	neuBew: number;

	// Legacy compatibility - alias property used by UI store joins on agency id.
	agtId: number;

	constructor(data: any) {
		this.vtnrId = data.aid || 0;
		this.kw = data.kw ? parseInt(data.kw) : 0;
		this.brutto = data.brutto || 0;
		this.netto = data.netto || 0;
		this.converted = data.converted || 0;
		this.neuStck = data.neu_stck || 0;
		this.neuBew = data.neu_bew || 0;
		this.agtId = this.vtnrId;
	}
}

export class AgtLeadNowConvStore extends AgtAnalyticsStore {
	constructor(session: SessionStore) {
		super(session, {
			tableName: 'agentur_leadnowconv',
			defaultColumns: [
				{ column: 'vtnr_id', aggregateFunction: AggregateFunction.NONE, alias: 'aid' },
				{ column: 'kw', aggregateFunction: AggregateFunction.NONE },
				{ column: 'brutto', aggregateFunction: AggregateFunction.NONE },
				{ column: 'netto', aggregateFunction: AggregateFunction.NONE },
				{ column: 'converted', aggregateFunction: AggregateFunction.NONE },
				{ column: 'neu_stck', aggregateFunction: AggregateFunction.NONE },
				{ column: 'neu_bew', aggregateFunction: AggregateFunction.NONE },
			],
		});
		this.queryName = '?leadNowConv';
	}

	async getDistinctKWs(): Promise<number[]> {
		const selectColumns: SelectColumn[] = [
			{ column: 'kw', aggregateFunction: AggregateFunction.NONE, distinct: true },
		];
		const results = await this.query(selectColumns);
		return results.map((data) => parseInt(data.kw)).sort((a, b) => a - b);
	}

	@action
	async findByKw(kw: number): Promise<AgtLeadNowConvModel[]> {
		const results = await this.queryWithWhere(`kw = ${kw}`);
		return results.map((data) => new AgtLeadNowConvModel(data));
	}

	@action
	async aggregateByKwRange(minKw: number, maxKw: number): Promise<AgtLeadNowConvModel[]> {
		const whereClause = `kw BETWEEN ${minKw} AND ${maxKw}`;
		const selectColumns: SelectColumn[] = [
			{ column: 'vtnr_id', aggregateFunction: AggregateFunction.NONE, groupBy: true, alias: 'aid' },
			{ column: 'kw', aggregateFunction: AggregateFunction.MAX, alias: 'kw' },
			{ column: 'brutto', aggregateFunction: AggregateFunction.SUM, alias: 'brutto' },
			{ column: 'netto', aggregateFunction: AggregateFunction.SUM, alias: 'netto' },
			{ column: 'converted', aggregateFunction: AggregateFunction.SUM, alias: 'converted' },
			{ column: 'neu_stck', aggregateFunction: AggregateFunction.SUM, alias: 'neu_stck' },
			{ column: 'neu_bew', aggregateFunction: AggregateFunction.SUM, alias: 'neu_bew' },
		];

		const results = await this.query(selectColumns, whereClause);
		return results.map((data) => new AgtLeadNowConvModel(data));
	}

	/**
	 * Anonymous VD/ABV benchmark totals over a kw range. Backed by a dedicated,
	 * deliberately unscoped endpoint that returns only aggregate sums (no per-agency
	 * rows), so it can show averages beyond the caller's own agency scope.
	 */
	@action
	async getBenchmark(minKw: number, maxKw: number, vdId?: string): Promise<ILeadNowConvBenchmark> {
		const res = await this.post('/leadnowconv/benchmark', { minKw, maxKw, vdId });
		return (res.data || {}) as ILeadNowConvBenchmark;
	}
}
